/**
 * AURA Context Layer
 *
 * Makes the product self-explanatory on every visit — not just the first.
 * Permanent, ambient, non-intrusive context woven into the chrome:
 *
 * 1. Top-left context label — always shows what the current mode does
 * 2. Mode nav subtitles — always visible 1-line description under each mode
 * 3. Entry explanation — shows with the entry ritual, auto-fades
 */

import { useAuraStore, type FlyWheelMode } from '../state/store';

const MODE_DESCRIPTIONS: Partial<Record<FlyWheelMode, { verb: string; noun: string }>> = {
  sense:    { verb: 'Reading', noun: 'intelligence brief' },
  validate: { verb: 'Testing', noun: 'signal quality' },
  field:    { verb: 'Opening', noun: 'live activation' },
};

const MODE_SUBTITLES: Partial<Record<FlyWheelMode, string>> = {
  sense:    'Read the brief',
  validate: 'Test signal quality',
  field:    'Activate a cluster',
};

export class Onboarding {
  private contextLabel: HTMLElement | null = null;
  private lastMode: FlyWheelMode | null = null;

  constructor() {
    this.injectStyles();
    this.addModeSubtitles();
    this.addContextLabel();
    this.addEntryExplanation();
  }

  private injectStyles() {
    const style = document.createElement('style');
    style.textContent = `
      /* Top-left context label — finding 11: raised to legible */
      #aura-context-label {
        position: fixed;
        top: 22px;
        left: 80px;
        z-index: 100;
        font-family: 'Inter', -apple-system, sans-serif;
        opacity: 0;
        transition: opacity 0.6s ease;
        pointer-events: none;
      }
      #aura-context-label.visible { opacity: 1; }
      .acl-mode {
        font-size: 9px;
        font-weight: 700;
        letter-spacing: 0.18em;
        text-transform: uppercase;
        color: rgba(255,255,255,0.30); /* raised from 0.22 */
        line-height: 1;
      }
      .acl-action {
        font-size: 11px;
        font-weight: 400;
        color: rgba(255,255,255,0.48); /* raised from 0.38 */
        margin-top: 3px;
        letter-spacing: 0.01em;
      }

      /* Mode nav subtitles — finding 12: 8px→9px, legible differential */
      .mode-subtitle {
        display: block;
        font-size: 9px;
        font-weight: 400;
        color: rgba(255,255,255,0.25);
        letter-spacing: 0.03em;
        text-transform: none;
        margin-top: 3px;
        transition: color 0.4s ease;
      }
      .mode-label.active .mode-subtitle { color: rgba(255,255,255,0.38); }
      .mode-label:hover .mode-subtitle { color: rgba(255,255,255,0.35); }

      /* Entry explanation */
      #aura-entry-explanation {
        position: fixed;
        bottom: 140px;
        left: 50%;
        transform: translateX(-50%);
        text-align: center;
        z-index: 50;
        opacity: 0;
        transition: opacity 1s ease;
        pointer-events: none;
        font-family: 'Inter', sans-serif;
        width: 420px;
      }
      #aura-entry-explanation.visible { opacity: 1; }
      .aee-line1 {
        font-size: 14px;
        font-weight: 400;
        color: rgba(255,255,255,0.55);
        letter-spacing: 0.01em;
        margin-bottom: 6px;
      }
      .aee-line2 {
        font-size: 11px;
        font-weight: 400;
        color: rgba(255,255,255,0.25);
        letter-spacing: 0.02em;
        line-height: 1.6;
      }
    `;
    document.head.appendChild(style);
  }

  private addModeSubtitles() {
    // Add a subtitle line under each mode label — always visible, not just on hover
    const labels = document.querySelectorAll('.mode-label');
    labels.forEach(label => {
      const mode = label.getAttribute('data-mode') as FlyWheelMode | null;
      if (!mode || !MODE_SUBTITLES[mode]) return;

      const sub = document.createElement('span');
      sub.className = 'mode-subtitle';
      sub.textContent = MODE_SUBTITLES[mode]!;
      label.appendChild(sub);
    });
  }

  private addContextLabel() {
    this.contextLabel = document.createElement('div');
    this.contextLabel.id = 'aura-context-label';
    document.body.appendChild(this.contextLabel);
  }

  private addEntryExplanation() {
    const el = document.createElement('div');
    el.id = 'aura-entry-explanation';
    el.innerHTML = `
      <div class="aee-line1">Each node is a cluster of real signals — hover to explore, click to read the brief.</div>
      <div class="aee-line2">
        Signals come from web, news, and events.<br>
        The field shows what's rising and what to activate.
      </div>
    `;
    document.body.appendChild(el);

    // Show after entry ritual
    setTimeout(() => el.classList.add('visible'), 3000);
    // Fade after 9s
    setTimeout(() => {
      el.style.transition = 'opacity 1.5s ease';
      el.style.opacity = '0';
      setTimeout(() => el.remove(), 1600);
    }, 9500);
  }

  update() {
    if (!this.contextLabel) return;
    const mode = useAuraStore.getState().mode as FlyWheelMode;
    if (mode === this.lastMode) return;
    this.lastMode = mode;

    const desc = MODE_DESCRIPTIONS[mode];
    if (!desc) { this.contextLabel.classList.remove('visible'); return; }

    this.contextLabel.innerHTML = `
      <div class="acl-mode">${mode}</div>
      <div class="acl-action">${desc.verb} the ${desc.noun}</div>
    `;
    this.contextLabel.classList.add('visible');
  }

  onLiveDataLoaded(clusterCount: number) {
    // Update the entry explanation with real cluster count
    const el = document.getElementById('aura-entry-explanation');
    if (!el || clusterCount === 0) return;
    const line = el.querySelector('.aee-line1');
    if (line) {
      line.textContent = `${clusterCount} intelligence clusters tracking Soho's domains — hover any node to explore, click to read the brief.`;
    }
  }
}
